import React, { useState, useEffect, useCallback } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import { Link, useNavigate } from "react-router-dom";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import MarketingTabs from "../AllTabs/MarketingTabs";

const initialFieldValues = {
  customerId: "00000000-0000-0000-0000-000000000000",
  companyId: "00000000-0000-0000-0000-000000000000",
  customerName: "",
  phoneNumber: "",
  email: "",
};

const Customer = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addShow, setAddShow] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Search and filter states - similar to Users component
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");

  const handleOpenDeleteModal = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleAddClose = () => {
    setAddShow(false);
    setRecordForEdit(null);
  };

  const handleAddShow = () => {
    setRecordForEdit(null);
    setValues(initialFieldValues);
    setAddShow(true);
    setErrors({});
  };

  useEffect(() => {
    if (recordForEdit !== null) {
      setValues(recordForEdit);
    }
  }, [recordForEdit]);

  // Updated GetData to include search and filters - similar to Users component
  const GetData = async (currentPage = 1, search = "", status = "") => {
    if (!user) return;

    try {
      // Build query parameters like Users component
      let apiUrl = `${config.GETCUSTOMERBYCOMAPANYID}/${user?.cid}?roleName=CUSTOMER&PageNumber=${currentPage}&PageSize=${pageSize}`;

      // Add search term if provided
      if (search) {
        apiUrl += `&searchTerm=${encodeURIComponent(search)}`;
      }

      // Add status filter if provided
      if (status !== "") {
        apiUrl += `&isActive=${status}`;
      }

      const response = await apiService.getWithParams(apiUrl);

      console.log("Fetched customers:", response);
      const customersData = response.data || [];
      setLists(customersData);
      setFilteredLists(customersData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
      setPageSize(response.pageSize || 20);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setLists([]);
      setFilteredLists([]);
    }
  };

  // Generate search suggestions - similar to Users component
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const nameMatches = lists
      .filter((customer) =>
        customer.customerName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5);

    const emailMatches = lists
      .filter(
        (customer) =>
          customer.email?.toLowerCase().includes(searchLower) &&
          !customer.customerName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const phoneMatches = lists
      .filter(
        (customer) =>
          customer.phoneNumber?.includes(searchValue) &&
          !customer.customerName?.toLowerCase().includes(searchLower) &&
          !customer.email?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [...nameMatches, ...emailMatches, ...phoneMatches];
    setSuggestions(allSuggestions);
  };

  // Handle search - similar to Users component
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredLists(lists);
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = lists.filter(
        (customer) =>
          customer.customerName?.toLowerCase().includes(value.toLowerCase()) ||
          customer.email?.toLowerCase().includes(value.toLowerCase()) ||
          customer.phoneNumber?.includes(value)
      );
      setFilteredLists(filtered);
    }
  };

  // Handle suggestion click - similar to Users component
  const handleSuggestionClick = (customer) => {
    setSearchTerm(customer.customerName);
    setFilteredLists([customer]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Handle status filter change
  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setPageNumber(1);
    GetData(1, searchTerm, value);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setStatusFilter("");
    setSearchTerm("");
    setPageNumber(1);
    GetData(1, "", "");
  };

  const GetLastPageData = () => {
    GetData(totalPages, searchTerm, statusFilter);
  };

  const GetFirstPageData = () => {
    GetData(1, searchTerm, statusFilter);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number, searchTerm, statusFilter);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  const showEditDetails = (data) => {
    setRecordForEdit(data);
    setValues(data);
    setAddShow(true);
    setErrors({});
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETECUSTOMER, itemToDelete);
        await GetData(pageNumber, searchTerm, statusFilter);
      } catch (error) {
        console.error("Error deleting customer:", error);
      }
      handleCloseDeleteModal();
    }
  };

  const validate = () => {
    let temp = {};

    // Name validation - only letters, spaces, and certain special characters allowed
    const namePattern = /^[a-zA-Z\s.'-]+$/;
    const emailPattern = /@/;
    const phonePattern = /^\d+$/;

    temp.customerName =
      values.customerName === ""
        ? false
        : namePattern.test(values.customerName) &&
          !emailPattern.test(values.customerName) &&
          !phonePattern.test(values.customerName.replace(/\s/g, ""));

    // Phone number validation - only digits, 10-15 characters
    const phoneValidPattern = /^\d{10,15}$/;
    temp.phoneNumber =
      values.phoneNumber === ""
        ? false
        : phoneValidPattern.test(values.phoneNumber);

    // Email validation with regex pattern
    const emailValidPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    temp.email =
      values.email === "" ? false : emailValidPattern.test(values.email);

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      var formData = {
        customerId: values.customerId,
        customerName: values.customerName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        companyId: user?.cid,
        roleName: "CUSTOMER", // Static role name
      };
      try {
        const isNew =
          formData.customerId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATECUSTOMER, formData);
        } else {
          await apiService.update(config.UPDATECUSTOMER, formData);
        }
        await GetData(pageNumber, searchTerm, statusFilter);
        resetForm();
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const resetForm = () => {
    setValues(initialFieldValues);
    setAddShow(false);
    setBtnLoading(false);
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  useEffect(() => {
    if (user && !loading) {
      GetData(pageNumber, searchTerm, statusFilter);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <MarketingTabs pageName="CUSTOMERS" />
                </div>

                {/* Search and Filter Section - Similar to Users component */}
                <div className="card-body">
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="card-title mb-0">Customer Management</h5>
                        <button
                          className="btn btn-primary"
                          onClick={handleAddShow}
                        >
                          <i className="ri-add-line me-2"></i>
                          Add Customer
                        </button>
                      </div>

                      {/* Filter Section */}
                      <div
                        className="row align-items-end mb-4"
                        style={{ gap: "8px 0" }}
                      >
                        {/* Search Bar - wider column */}
                        <div className="col-md-6">
                          <div style={{ position: "relative" }}>
                            <div style={{ position: "relative" }}>
                              <i
                                className="ri-search-line"
                                style={{
                                  position: "absolute",
                                  left: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  zIndex: 10,
                                  color: "#6c757d",
                                }}
                              ></i>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search by customer name, email, or phone..."
                                value={searchTerm}
                                onChange={handleSearch}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                                style={{
                                  height: "45px",
                                  paddingLeft: "40px",
                                  position: "relative",
                                }}
                              />
                              {searchTerm && (
                                <button
                                  className="btn position-absolute end-0 top-0 bottom-0"
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    zIndex: 10,
                                  }}
                                  onClick={() => setSearchTerm("")}
                                >
                                  <i className="ri-close-line fs-5"></i>
                                </button>
                              )}

                              {/* Suggestions dropdown */}
                              {showSuggestions && suggestions.length > 0 && (
                                <div
                                  style={{
                                    position: "absolute",
                                    width: "100%",
                                    marginTop: "4px",
                                    border: "1px solid #dee2e6",
                                    backgroundColor: "white",
                                    borderRadius: "6px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    zIndex: 1000,
                                  }}
                                >
                                  {suggestions.map((customer) => (
                                    <div
                                      key={customer.customerId}
                                      style={{
                                        padding: "8px 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        backgroundColor: "white",
                                        transition: "background-color 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor =
                                          "#f8f9fa";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor =
                                          "white";
                                      }}
                                      onClick={() =>
                                        handleSuggestionClick(customer)
                                      }
                                    >
                                      <i className="ri-user-line me-2 text-secondary"></i>
                                      <div>
                                        <div style={{ fontWeight: "600" }}>
                                          {customer.customerName}
                                        </div>
                                        {customer.email && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {customer.email}
                                          </div>
                                        )}
                                        {customer.phoneNumber && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {customer.phoneNumber}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-2">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            style={{ height: "45px" }}
                          >
                            <option value="">All Status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </div>

                        {/* Reset Button */}
                        <div className="col-md-1">
                          <button
                            className="btn btn-danger w-100"
                            onClick={handleResetFilters}
                            style={{ height: "45px" }}
                          >
                            <i className="ri-refresh-line me-1"></i>
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table Section */}
                  <div className="mt-4">
                    <div className="table-responsive table-card p-2">
                      <table className="table align-middle" id="customerTable">
                        <thead className="table-light">
                          <tr>
                            <th>S.No</th>
                            <th>Customer Name</th>
                            <th>Phone Number</th>
                            <th>Email</th>

                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {filteredLists.length > 0 ? (
                            filteredLists.map((v, index) => (
                              <tr key={v.customerId}>
                                <td>
                                  {(pageNumber - 1) * pageSize + index + 1}
                                </td>
                                <td>{v.customerName}</td>
                                <td>{v.phoneNumber}</td>
                                <td>{v.email}</td>

                                <td>
                                  <span
                                    className={`badge ${
                                      v.isActive ? "bg-success" : "bg-danger"
                                    } fs-6`}
                                  >
                                    {v.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <ul className="list-inline hstack gap-2 mb-0">
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="Edit"
                                    >
                                      <Link
                                        className="edit-item-btn"
                                        onClick={(e) => showEditDetails(v)}
                                      >
                                        <i className="ri-pencil-fill align-bottom text-muted" />
                                      </Link>
                                    </li>
                                    <li
                                      className="list-inline-item"
                                      data-bs-toggle="tooltip"
                                      data-bs-trigger="hover"
                                      data-bs-placement="top"
                                      title="Delete"
                                    >
                                      <Link
                                        className="remove-item-btn"
                                        onClick={(e) =>
                                          handleOpenDeleteModal(v.customerId)
                                        }
                                      >
                                        <i className="ri-delete-bin-fill align-bottom text-muted" />
                                      </Link>
                                    </li>
                                  </ul>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="ri-user-search-line fs-1"></i>
                                  <p className="mt-2">
                                    {searchTerm || statusFilter
                                      ? "No customers found matching your criteria."
                                      : "No customers found."}
                                  </p>
                                  {(searchTerm || statusFilter) && (
                                    <button
                                      className="btn btn-sm btn-outline-primary mt-2"
                                      onClick={handleResetFilters}
                                    >
                                      Clear Filters
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {filteredLists.length > 0 && (
                    <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                      <div className="flex-shrink-0">
                        <div className="text-muted">
                          Showing{" "}
                          <span className="fw-semibold">
                            {filteredLists.length}
                          </span>{" "}
                          of <span className="fw-semibold">{totalRecords}</span>{" "}
                          Customers
                        </div>
                      </div>
                      <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li
                          className={`page-item ${
                            pageNumber === 1 ? "disabled" : ""
                          }`}
                          onClick={() => pageNumber !== 1 && GetFirstPageData()}
                        >
                          <Link className="page-link">First</Link>
                        </li>
                        {renderPageNumbers}
                        <li
                          className={`page-item ${
                            pageNumber === totalPages ? "disabled" : ""
                          }`}
                          onClick={() =>
                            pageNumber !== totalPages && GetLastPageData()
                          }
                        >
                          <Link className="page-link">Last</Link>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Customer Modal */}
        <Modal show={addShow} onHide={handleAddClose}>
          <Modal.Header>
            <Modal.Title>
              {values.customerId === "00000000-0000-0000-0000-000000000000"
                ? "Add"
                : "Edit"}{" "}
              Customer
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className="row">
                <div className="col-lg-12 mb-3">
                  <label htmlFor="customerName" className="form-label">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    maxLength={45}
                    value={values.customerName}
                    name="customerName"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("customerName")}
                    placeholder="Please Enter Customer Name"
                  />
                  {errors.customerName === false ? (
                    <div className="validationerror">
                      {values.customerName === ""
                        ? "Please enter customer name"
                        : "Please enter a valid name "}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-lg-12 mb-3">
                  <label htmlFor="phoneNumber" className="form-label">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={values.phoneNumber}
                    name="phoneNumber"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("phoneNumber")}
                    placeholder="Please Enter Phone Number"
                    maxLength={15}
                  />
                  {errors.phoneNumber === false ? (
                    <div className="validationerror">
                      {values.phoneNumber === ""
                        ? "Please enter phone number"
                        : "Please enter a valid phone number"}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <div className="col-lg-12 mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    type="email"
                    maxLength={45}
                    value={values.email}
                    name="email"
                    onChange={handleInputChange}
                    className={"form-control" + applyErrorClass("email")}
                    placeholder="Please Enter Email"
                  />
                  {errors.email === false ? (
                    <div className="validationerror">
                      {values.email === ""
                        ? "Please enter email"
                        : "Please enter a valid email address"}
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                <Modal.Footer>
                  <div className="hstack gap-2 justify-content-end mb-3 mt-4">
                    {btnLoading === false ? (
                      <button type="submit" className="btn btn-primary">
                        {values.customerId ===
                        "00000000-0000-0000-0000-000000000000"
                          ? "Submit"
                          : "Update"}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled
                      >
                        Please wait...
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </Modal.Footer>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>

      <style>{`
        .badge.bg-success {
          background: linear-gradient(45deg, #198754, #2ea65c);
        }
        .badge.bg-danger {
          background: linear-gradient(45deg, #dc3545, #e63749);
        }
        .badge.bg-info {
          background: linear-gradient(45deg, #0dcaf0, #25c2e9);
        }
        .badge {
          padding: 0.5em 0.8em;
          border-radius: 20px;
          font-weight: 500;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        
        .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default Customer;
